import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Footer from '@/components/Footer';
import PublicNav from '@/components/PublicNav';
import {
  Camera, Briefcase, FileText, Users, CheckCircle, DollarSign,
  MapPin, MessageSquare, Star, Shield, Smartphone, Clock,
  ArrowRight, Search, Send, ThumbsUp, CreditCard, Zap
} from 'lucide-react';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Navigation */}
      <PublicNav />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-[#0a0a0a] to-[#111111]">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            How <span className="text-[#7B3FF2]">Shootzu</span> Works
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
            Connect with professional photographers and videographers for any project
          </p>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Shootzu is a marketplace that connects talented photographers and videographers with
            businesses and individuals who need professional creative services. Think of it as the go-to
            platform for booking reliable, vetted creative talent for any event or project.
          </p>
        </div>
      </section>

      {/* For Businesses & Clients */}
      <section className="py-20 px-4 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#7B3FF2]/20 text-[#7B3FF2] px-4 py-2 rounded-full mb-4">
              <Briefcase className="w-5 h-5" />
              <span className="font-semibold">For Businesses & Clients</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Hire Creative Talent in 4 Simple Steps
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7B3FF2]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7B3FF2]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7B3FF2]/30 transition-colors">
                <FileText className="w-7 h-7 text-[#7B3FF2]" />
              </div>
              <div className="text-sm text-[#7B3FF2] font-semibold mb-2">Step 1</div>
              <h3 className="text-xl font-bold text-white mb-3">Post Your Job</h3>
              <p className="text-gray-400 leading-relaxed">
                Describe what you need—whether it's a wedding photographer, corporate event videographer,
                product shoot specialist, or real estate photographer. Include the date, location, hours required,
                and your budget. Our AI assistant can even help you write a clear, detailed job brief.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7B3FF2]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7B3FF2]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7B3FF2]/30 transition-colors">
                <Search className="w-7 h-7 text-[#7B3FF2]" />
              </div>
              <div className="text-sm text-[#7B3FF2] font-semibold mb-2">Step 2</div>
              <h3 className="text-xl font-bold text-white mb-3">Review Applications</h3>
              <p className="text-gray-400 leading-relaxed">
                Once your job goes live, local crew members who match your requirements will apply.
                Browse their portfolios, read reviews from previous clients, see their ratings, and check
                their experience before making your decision.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7B3FF2]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7B3FF2]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7B3FF2]/30 transition-colors">
                <Shield className="w-7 h-7 text-[#7B3FF2]" />
              </div>
              <div className="text-sm text-[#7B3FF2] font-semibold mb-2">Step 3</div>
              <h3 className="text-xl font-bold text-white mb-3">Hire with Confidence</h3>
              <p className="text-gray-400 leading-relaxed">
                When you find the right person, send them an offer. They'll confirm the booking, and you'll
                pay a 50% deposit upfront. The remaining 50% is charged before the job date. Your money
                is held securely until the work is completed.
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7B3FF2]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7B3FF2]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7B3FF2]/30 transition-colors">
                <ThumbsUp className="w-7 h-7 text-[#7B3FF2]" />
              </div>
              <div className="text-sm text-[#7B3FF2] font-semibold mb-2">Step 4</div>
              <h3 className="text-xl font-bold text-white mb-3">Get Great Results</h3>
              <p className="text-gray-400 leading-relaxed">
                After the job, leave a review to help other clients and to recognise great work.
                If anything goes wrong, our platform support team is here to help.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/login">
              <Button className="bg-[#7B3FF2] hover:bg-[#6930d1] text-white px-8 py-6 text-lg font-semibold">
                Post a Job Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* For Photographers & Videographers */}
      <section className="py-20 px-4 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-[#7DD3C0]/20 text-[#7DD3C0] px-4 py-2 rounded-full mb-4">
              <Camera className="w-5 h-5" />
              <span className="font-semibold">For Photographers & Videographers</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Find Work & Get Paid Reliably
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Step 1 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7DD3C0]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7DD3C0]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7DD3C0]/30 transition-colors">
                <MapPin className="w-7 h-7 text-[#7DD3C0]" />
              </div>
              <div className="text-sm text-[#7DD3C0] font-semibold mb-2">Step 1</div>
              <h3 className="text-xl font-bold text-white mb-3">Find Work Near You</h3>
              <p className="text-gray-400 leading-relaxed">
                Browse available jobs in your area—Shootzu uses smart location matching to show you
                gigs that make sense for you. Filter by job type, date, and pay rate to find the perfect fit.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7DD3C0]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7DD3C0]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7DD3C0]/30 transition-colors">
                <Send className="w-7 h-7 text-[#7DD3C0]" />
              </div>
              <div className="text-sm text-[#7DD3C0] font-semibold mb-2">Step 2</div>
              <h3 className="text-xl font-bold text-white mb-3">Apply & Get Hired</h3>
              <p className="text-gray-400 leading-relaxed">
                Submit applications for jobs that interest you. Your profile, portfolio, and reviews do the
                selling for you. When a client likes what they see, they'll send you an offer to accept.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7DD3C0]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7DD3C0]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7DD3C0]/30 transition-colors">
                <Camera className="w-7 h-7 text-[#7DD3C0]" />
              </div>
              <div className="text-sm text-[#7DD3C0] font-semibold mb-2">Step 3</div>
              <h3 className="text-xl font-bold text-white mb-3">Do What You Love</h3>
              <p className="text-gray-400 leading-relaxed">
                Turn up, do great work, and deliver professional results. The platform handles the
                business side—contracts, payments, and scheduling—so you can focus on creating.
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800 hover:border-[#7DD3C0]/50 transition-all group">
              <div className="w-14 h-14 bg-[#7DD3C0]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7DD3C0]/30 transition-colors">
                <DollarSign className="w-7 h-7 text-[#7DD3C0]" />
              </div>
              <div className="text-sm text-[#7DD3C0] font-semibold mb-2">Step 4</div>
              <h3 className="text-xl font-bold text-white mb-3">Get Paid Reliably</h3>
              <p className="text-gray-400 leading-relaxed">
                Once the job is complete and confirmed, funds are released to your bank account.
                The platform takes a small 2% service fee, and you keep the rest. No chasing invoices,
                no awkward money conversations.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/login">
              <Button className="bg-[#7DD3C0] hover:bg-[#6bc4b1] text-[#0a0a0a] px-8 py-6 text-lg font-semibold">
                Join as Crew
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 px-4 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Platform Features
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Everything you need to connect, create, and get paid
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Smart Matching */}
            <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
              <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#7DD3C0] rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Smart Matching</h3>
              <p className="text-gray-400">
                Jobs automatically find crew members nearby based on skills, availability, and location.
              </p>
            </div>

            {/* Secure Payments */}
            <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
              <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#7DD3C0] rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Secure Payments</h3>
              <p className="text-gray-400">
                Client funds are held in escrow and released upon job completion. Both parties are protected.
              </p>
            </div>

            {/* Real-Time Messaging */}
            <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
              <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#7DD3C0] rounded-lg flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Real-Time Messaging</h3>
              <p className="text-gray-400">
                Communicate directly through the app to coordinate details, share shot lists, and confirm logistics.
              </p>
            </div>

            {/* Reviews & Ratings */}
            <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
              <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#7DD3C0] rounded-lg flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Reviews & Ratings</h3>
              <p className="text-gray-400">
                A two-way review system builds trust and helps quality rise to the top.
              </p>
            </div>

            {/* Flexible Job Types */}
            <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
              <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#7DD3C0] rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Flexible Job Types</h3>
              <p className="text-gray-400">
                From quick half-day shoots to multi-day events requiring multiple crew members, the platform handles it all.
              </p>
            </div>

            {/* Mobile App */}
            <div className="bg-[#1a1a1a] rounded-xl p-6 border border-gray-800">
              <div className="w-12 h-12 bg-gradient-to-br from-[#7B3FF2] to-[#7DD3C0] rounded-lg flex items-center justify-center mb-4">
                <Smartphone className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Mobile App</h3>
              <p className="text-gray-400">
                Manage jobs, respond to messages, and get notified about new opportunities—all from your phone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Simple, Transparent Fees
            </h2>
            <p className="text-gray-400 text-lg">
              No hidden charges. No subscription required. You only pay when work happens.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Clients */}
            <div className="bg-gradient-to-br from-[#7B3FF2]/20 to-[#7B3FF2]/5 rounded-2xl p-8 border border-[#7B3FF2]/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#7B3FF2] rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">For Clients</h3>
                  <p className="text-gray-400 text-sm">Businesses & Individuals</p>
                </div>
              </div>
              <div className="text-center py-8">
                <div className="text-5xl font-bold text-white mb-2">5%</div>
                <div className="text-gray-400">service fee</div>
              </div>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#7B3FF2]" />
                  Access to vetted professionals
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#7B3FF2]" />
                  Secure payment protection
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#7B3FF2]" />
                  Platform support
                </li>
              </ul>
            </div>

            {/* Crew */}
            <div className="bg-gradient-to-br from-[#7DD3C0]/20 to-[#7DD3C0]/5 rounded-2xl p-8 border border-[#7DD3C0]/30">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#7DD3C0] rounded-xl flex items-center justify-center">
                  <Camera className="w-6 h-6 text-[#0a0a0a]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">For Crew</h3>
                  <p className="text-gray-400 text-sm">Photographers & Videographers</p>
                </div>
              </div>
              <div className="text-center py-8">
                <div className="text-5xl font-bold text-white mb-2">2%</div>
                <div className="text-gray-400">service fee</div>
              </div>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#7DD3C0]" />
                  Free profile & portfolio
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#7DD3C0]" />
                  Guaranteed payment
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#7DD3C0]" />
                  No invoice chasing
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#7B3FF2] to-[#6930d1]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Whether you're looking to hire creative talent or find your next gig,
            Shootzu makes it simple.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button className="bg-white text-[#7B3FF2] hover:bg-gray-100 px-8 py-6 text-lg font-semibold w-full sm:w-auto">
                <Briefcase className="mr-2 w-5 h-5" />
                Hire Crew
              </Button>
            </Link>
            <Link to="/login">
              <Button className="bg-[#0a0a0a] text-white hover:bg-[#1a1a1a] px-8 py-6 text-lg font-semibold w-full sm:w-auto">
                <Camera className="mr-2 w-5 h-5" />
                Join as Crew
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorks;
